/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import './ExclusiveBenefitsCards.scss';

import React, { Fragment } from 'react';
import { renderToString } from 'react-dom/server';

import { ReactComponent as DollarArrow } from 'assets/icons/dollar-arrow-circle.svg';
import { ReactComponent as MutualFund } from 'assets/icons/mutual-fund.svg';
import { ReactComponent as Book } from 'assets/icons/book.svg';
import { summitMembershipMapById } from 'services/HouseholdInformationCard';

import {
  CORE,
  SELECT,
  PERSONAL_SERVICES,
  ENHANCED_PERSONAL_SERVICES
} from 'services/HouseholdInformationCard';
import { transactions, lifeEventsManagerLink } from 'services/Externals';
import { GLOBALS } from 'services/globals';
import { kiplingerLink } from 'components/layouts/HouseholdInformationCard/HouseholdInformationCard';
import { MorningstarRegistration } from 'components/layouts/HouseholdInformationCard/MorningstarRegistration';
import MobileBenefitsDropdown from './MobileBenefitsDropdown';
import TRP from 'components/shared/TRP/TRP';

const BenefitsCard = ({
  icon: Icon,
  title,
  description,
  footer,
  isPremiumInvestorTile,
  isEPS,
  isMobile
}) => {
  return isMobile ? (
    <MobileBenefitsDropdown
      title={title}
      description={description}
      footer={footer}
      isPremiumInvestorTile={isPremiumInvestorTile}
    />
  ) : (
    <Fragment>
      <div className="benefits-card-container">
        <div className="benefits-card-header">
          <Icon className="benefits-card-icon" />
        </div>
        <div
          className={`benefits-card-body ${
            isPremiumInvestorTile
              ? `premium-investor ${isEPS ? 'eps' : ''}`
              : ''
          }`}>
          <p className="benefits-card-title">{title}</p>
          {description && (
            <div
              className="benefits-card-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
        {footer && <div className="benefits-card-footer">{footer}</div>}
      </div>
    </Fragment>
  );
};

BenefitsCard.defaultProps = {
  description: '',
  isPremiumInvestorTile: false,
  isEPS: false,
  isMobile: false
};

const benefitsDescription = {
  iClassShares: `<p>Pay lower costs when you convert to I Class shares from Investor Class shares.<sup>1</sup> Learn more about our <a class="link" href="${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/tools/fund-research/iclass-shares">I Class shares</a>.</p>
<p>Automated Conversions: Eligible holdings will be automatically converted from Investor Class shares to I Class shares on a periodic basis. <a class="link" href="${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/about/client-benefits/faqs.html#conversion-process"> Learn more about the conversion process.</a></p>
`,
  lifeEventCaseManager:
    'Work with a financial professional who will help you navigate a major change in your life,' +
    ' like changing jobs, getting married, sending a child off to college, or caring for a loved one.',
  closedFunds:
    `Get preferred access to invest in ${renderToString(
      <TRP />
    )} mutual funds that are not generally available` +
    ` to new investors. Learn more about our <a class="link" href="${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/tools/fund-research/closed-funds">closed funds</a>.`
};

const footer = {
  iClassShares: (
    <a href={transactions.mutualFundNonRetirement.add}>Buy I-Class</a>
  ),
  lifeEventCaseManager: <a href={lifeEventsManagerLink}>Get Support</a>,
  closedFunds: (
    <a href={transactions.mutualFundNonRetirement.add}>Buy Closed Funds</a>
  )
};

const premiumContentPS = () => {
  return (
    <Fragment>
      <div className="morningstar-link-ps">
        <MorningstarRegistration />
      </div>
    </Fragment>
  );
};

const premiumContentEPS = (
  kiplingerAccess,
  kiplingerRegistrationUrl,
  isCustomerWebView
) => {
  return (
    <Fragment>
      <MorningstarRegistration />
      {kiplingerLink(
        kiplingerAccess,
        kiplingerRegistrationUrl,
        isCustomerWebView
      )}
      <div className="premium-content-eps-footer">
        Receive access to investing, retirement, and personal finance events
        featuring <TRP /> experts.
      </div>
    </Fragment>
  );
};

const ExclusiveBenefitsCards = ({ data, isMobile }) => {
  const {
    assetId,
    kiplingerAccess,
    kiplingerRegistrationUrl,
    isCustomerWebView
  } = data;

  const isCore = assetId === CORE;
  const isSelect = assetId === SELECT;
  const isPS = assetId === PERSONAL_SERVICES;
  const isEPS = assetId === ENHANCED_PERSONAL_SERVICES;

  return isCore ? null : (
    <Fragment>
      <p className="benefits-page-title">
        Your Benefits as a {summitMembershipMapById[assetId]} Member
      </p>
      <div className="exclusive-benefits-cards-container">
        {(isPS || isEPS) && (
          <BenefitsCard
            icon={DollarArrow}
            title="I Class Shares"
            description={benefitsDescription.iClassShares}
            footer={footer.iClassShares}
            isMobile={isMobile}
          />
        )}
        {(isSelect || isPS || isEPS) && (
          <BenefitsCard
            icon={MutualFund}
            title="Closed Funds"
            description={benefitsDescription.closedFunds}
            footer={footer.closedFunds}
            isMobile={isMobile}
          />
        )}
        <BenefitsCard
          icon={Book}
          title="Premium Investor Content"
          description=""
          footer={
            isSelect || isPS
              ? premiumContentPS()
              : premiumContentEPS(
                  kiplingerAccess,
                  kiplingerRegistrationUrl,
                  isCustomerWebView
                )
          }
          isPremiumInvestorTile={true}
          isMobile={isMobile}
          isEPS={isEPS}
        />
      </div>
    </Fragment>
  );
};

export default ExclusiveBenefitsCards;
